import { useEffect, useState } from "react";
import { Smartphone, RefreshCw, Loader2, Check, X, Mail, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";

function Line({ ok, label, detail, testid }) {
  return (
    <div className="flex items-start gap-2 text-xs" data-testid={testid}>
      {ok ? <Check className="mt-0.5 h-3.5 w-3.5 text-emerald-600" /> : <X className="mt-0.5 h-3.5 w-3.5 text-slate-400" />}
      <div><span className={ok ? "text-slate-900" : "text-slate-500"}>{label}</span>{detail && <div className="text-[11px] text-slate-400">{detail}</div>}</div>
    </div>
  );
}

function ContactRow({ studentId, r, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const send = async (extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/students/${studentId}/invite-parents`, { origin_url: window.location.origin, contact_index: r.contact_index, phone: r.phone || "", name: r.name || "", ...extra });
      const next = data.results[0];
      onUpdate(next);
      if (next?.skipped) toast.error(next.skipped); else toast.success(`Invite sent to ${next.contact}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invite failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`invite-contact-${r.contact_index}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-slate-900">{r.contact}{r.phone ? <span className="ml-2 font-normal text-slate-400">{r.phone}</span> : null}</div>
        {r.already_active && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-200"><UserCheck className="h-3 w-3" /> Has account</span>}
      </div>
      {r.skipped ? <div className="mt-1.5 text-xs text-amber-700" data-testid={`invite-skipped-${r.contact_index}`}>{r.skipped}</div> : (
        <div className="mt-2 space-y-1">
          <Line ok={r.sms_sent} label={r.sms_sent ? "Text message sent" : "Text message not sent"} detail={r.sms_error} testid={`invite-sms-status-${r.contact_index}`} />
          <Line ok={r.email_sent} label={r.email_sent ? `Email sent to ${r.email_to}` : r.email_to ? `Email to ${r.email_to} failed` : "No email on file for this contact"} detail={r.email_error} testid={`invite-email-status-${r.contact_index}`} />
        </div>
      )}
      {!r.skipped && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button onClick={() => send()} disabled={busy} data-testid={`invite-resend-${r.contact_index}`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-800 transition hover:bg-slate-100 disabled:opacity-60">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Resend
          </button>
          <div className="flex min-w-[220px] flex-1 items-center gap-1.5">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Send to a different email…" data-testid={`invite-email-input-${r.contact_index}`}
              className="h-9 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-xs outline-none focus:border-primary" />
            <button onClick={() => email && send({ email })} disabled={busy || !email} data-testid={`invite-email-send-${r.contact_index}`}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-slate-900 px-3 text-xs font-semibold text-white disabled:opacity-50">
              <Mail className="h-3.5 w-3.5" /> Email
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Results of inviting a child's pickup contacts to the parent app, with per-contact resend. */
export function ParentInvitesDialog({ data, onClose }) {
  const [results, setResults] = useState(data?.results || []);
  useEffect(() => { setResults(data?.results || []); }, [data]);
  if (!data) return null;
  const update = (next) => setResults((rs) => rs.map((r) => (r.contact_index === next.contact_index ? next : r)));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="parent-invites-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Smartphone className="h-5 w-5 text-primary" /> Parent app invites · {data.student_name}</DialogTitle>
          <DialogDescription>Each contact opens the emailed or texted link, sets a password and signs in with their mobile number. Links work for 30 days.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {results.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500" data-testid="invite-no-contacts">No pickup contacts on this child yet — edit the child and add a parent with a phone number.</div>}
          {results.map((r) => <ContactRow key={r.contact_index} studentId={data.student_id} r={r} onUpdate={update} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
